import { APP_INITIALIZER, ModuleWithProviders, NgModule } from '@angular/core';
import { AuthGuard } from '../../guards/auth.guard';
import { KeycloakAuthService } from './services/keycloak-auth.service';
import { KeycloakDynamicConfigService } from './services/keycloak-dynamic-config.service';
import { InactivityTimeDialogModule } from '../../shared/inactivity-time-dialog/inactivity-time-dialog.module';
import { StoreModule } from '@ngrx/store';
import { authReducer, featureName } from '../../store/auth.reducer';
import { OAuthModule, OAuthService } from 'angular-oauth2-oidc';
import { EffectsModule } from '@ngrx/effects';
import { KeycloakAuthEffects } from './store/keycloak-auth.effects';
import { IframeAuthEffects } from '../../store/iframe-auth.effects';
import { AuthService } from '../../services/auth.service';
import { KeycloakAuthRoutingModule } from './keycloak-auth-routing.module';
import { TenantService } from 'src/app/tenants/services/tenant.service';
import { BrandingService } from '../../../branding/services/branding.service';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ClientNames } from '../../../clients/client-names.enum';
import { Es01ClientGlobalStore } from '../../../clients/es01/store/es01-client-global.store';

type Es01ClientStore = InstanceType<typeof Es01ClientGlobalStore>;

/**
 * Load the es-01 clients collection when running the consoleES01 app, so brand /
 * region resolution works from the remote collection. No-op for other apps.
 */
function loadEs01CollectionIfNeeded(
  es01ClientStore: Es01ClientStore
): Promise<void> {
  if (environment.appName === ClientNames.consoleES01) {
    return es01ClientStore.loadCollection();
  }
  return Promise.resolve();
}

/**
 * Factory function to initialize Keycloak configuration
 * This will be called during app initialization
 */
export function initializeKeycloakDynamicConfig(
  dynamicConfigService: KeycloakDynamicConfigService,
  oauthService: OAuthService,
  es01ClientStore: Es01ClientStore
): () => Promise<void> {
  return async (): Promise<void> => {
    try {
      // es-01 resolves brandCode from the remote collection; load it before
      // keycloak config so the matched brandCode is available.
      await loadEs01CollectionIfNeeded(es01ClientStore);

      const dynamicConfig = await firstValueFrom(
        dynamicConfigService.fetchKeycloakConfig()
      );
      const authConfig = dynamicConfigService.createAuthConfig(dynamicConfig);
      oauthService.configure(authConfig);
      console.log('✅ Dynamic Keycloak configuration applied:', authConfig);
    } catch (error) {
      console.error(
        '❌ Failed to initialize dynamic Keycloak configuration:',
        error
      );

      sessionStorage.setItem(
        'keycloak-config-error',
        JSON.stringify({
          error: error?.message || 'Configuration failed',
          timestamp: new Date().toISOString(),
          details: error,
        })
      );
    }
  };
}

export function initializeKeycloakConditionalConfig(
  dynamicConfigService: KeycloakDynamicConfigService,
  oauthService: OAuthService,
  es01ClientStore: Es01ClientStore
): () => Promise<void> {
  if (window.self !== window.top) {
    // Embedded (iframe): keycloak is skipped, but es-01 still needs the
    // collection so host-based brand/region resolution works (Option A).
    return () => loadEs01CollectionIfNeeded(es01ClientStore);
  }
  return initializeKeycloakDynamicConfig(
    dynamicConfigService,
    oauthService,
    es01ClientStore
  );
}

@NgModule({
  imports: [
    OAuthModule.forRoot(),
    StoreModule.forFeature(featureName, authReducer),
    EffectsModule.forFeature(
      window.self !== window.top ? [IframeAuthEffects] : [KeycloakAuthEffects]
    ),
    InactivityTimeDialogModule,
    KeycloakAuthRoutingModule,
  ],
  providers: [
    KeycloakAuthService,
    KeycloakDynamicConfigService,
    AuthGuard,
    AuthService,
    TenantService,
    BrandingService,
  ],
})
export class KeycloakDynamicAuthModule {
  static forRoot(): ModuleWithProviders<KeycloakDynamicAuthModule> {
    return {
      ngModule: KeycloakDynamicAuthModule,
      providers: [
        {
          provide: APP_INITIALIZER,
          useFactory: initializeKeycloakConditionalConfig,
          deps: [
            KeycloakDynamicConfigService,
            OAuthService,
            Es01ClientGlobalStore,
          ],
          multi: true,
        },
      ],
    };
  }
}
